import React, { FC } from 'react';
import { Button, InputAdornment, TextField } from '@mui/material';
import CopyIcon from '../../../../components/icon/CopyIcon';
import LinkIcon from '../../../../components/icon/LinkIcon';
import ZenQRCode from '../../../../components/qrcode/ZenQRCode';
import DownloadIcon from '../../../../components/icon/DownloadIcon';
import copy from 'copy-to-clipboard';
import { enqueueSnackbar } from 'notistack';
import EmbedIcon from '../../../../components/icon/EmbedIcon';
import { downloadQRCode } from '../../../../shared/utils/file/file';
import TickCheckIcon from '../../../../components/icon/TickCheck';
import classNames from 'classnames';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useParams } from 'react-router-dom';
import Dialog, { DialogProps } from '../../../../components/dialog/Dialog';
import { LabelIcon } from '@mnz/element';

export interface ShareModalProps extends DialogProps {
    name: string;
    url: string;
    isPublished?: boolean;
    dashboardUrl?: string;
}

const ShareModal: FC<ShareModalProps> = ({ name, url, isPublished = false, dashboardUrl = '', ...props }) => {
    const { accountId } = useParams<{ accountId: string }>();
    const embedCodeTmp = `<iframe width="100%" height="800" src="${url}" title="Menuzen" name="${name}" allowtransparency="true" allowfullscreen="true" frameborder="0"></iframe>`;
    const handleClickCopy = () => {
        copy(url);
        enqueueSnackbar('Link Copied!', { variant: 'success' });
    };
    const handleClickCopyEmbedCode = () => {
        copy(embedCodeTmp);
        enqueueSnackbar('Embed Code Copied!', { variant: 'success' });
    };
    const qrUrl = `${url}?utm_medium=Web&utm_source=QRScan&utm_term=${accountId}`;
    return (
        <Dialog anchor="bottom" title={isPublished ? '' : name} showClose={!isPublished} {...props}>
            <div className="mt-10 px-28 pb-40">
                {isPublished && (
                    <div className="mt-28 flex flex-col items-center">
                        <div className="w-24 h-24 rounded-circle text-white text-12 bg-success-mid flex items-center justify-center">
                            <TickCheckIcon />
                        </div>
                        <p className="mt-10 text-24 leading-28 font-bold text-center">{`${name} Menu has been published.`}</p>
                    </div>
                )}
                <div className={`text-center`}>
                    <p className={`text-primary-80 text-14`}>Share your menu so the world can see it!</p>
                </div>
                <div
                    className={classNames({
                        'flex items-center justify-between': true,
                        'mt-24': !isPublished,
                        'mt-16': isPublished,
                    })}
                >
                    <a target="_blank" rel="noopener noreferrer" href={`mailto:?to=&body=${url}`}>
                        <div className="w-50 h-50 mx-6 bg-started rounded-circle flex items-center justify-center">
                            <LabelIcon type={`outline`} icon="Email" size={20} />
                        </div>
                    </a>

                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
                    >
                        <div className="w-50 h-50 mx-6 bg-started rounded-circle flex items-center justify-center">
                            <LabelIcon type={`outline`} icon="Facebook" size={20} />
                        </div>
                    </a>

                    <a target="_blank" rel="noopener noreferrer" href={`https://twitter.com/intent/tweet?url=${url}`}>
                        <div className="w-50 h-50 mx-6 bg-started rounded-circle flex items-center justify-center">
                            <LabelIcon type={`outline`} icon="Twitter" size={20} />
                        </div>
                    </a>

                    <a target="_blank" rel="noopener noreferrer" href={`https://api.whatsapp.com/send?text=${url}`}>
                        <div className="w-50 h-50 mx-6 bg-started rounded-circle flex items-center justify-center">
                            <LabelIcon type={`outline`} icon="Whatsapp" size={20} />
                        </div>
                    </a>

                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`https://www.facebook.com/dialog/send?app_id=${process.env.REACT_APP_FB_APP_ID}&link=${url}&redirect_uri=${url}`}
                    >
                        <div className="w-50 h-50 mx-6 bg-started rounded-circle flex items-center justify-center">
                            <LabelIcon type={`outline`} icon="Messenger" size={20} />
                        </div>
                    </a>
                </div>
                <div className={`mt-32`}>
                    <p className={`mb-10 text-14 leading-20 text-primary-80`}>Share URL</p>
                    <TextField
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LinkIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <div className="cursor-pointer" onClick={handleClickCopy}>
                                        <CopyIcon />
                                    </div>
                                </InputAdornment>
                            ),
                            readOnly: true,
                            sx: {
                                fontSize: 16,
                            },
                        }}
                        fullWidth
                        type="text"
                        defaultValue={url}
                    />
                </div>
                <div className={`mt-16`}>
                    <p className={`mb-10 text-14 leading-20 text-primary-80`}>Embed Code</p>
                    <TextField
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmbedIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <div className="cursor-pointer" onClick={handleClickCopyEmbedCode}>
                                        <CopyIcon />
                                    </div>
                                </InputAdornment>
                            ),
                            readOnly: true,
                            sx: {
                                fontSize: 16,
                            },
                        }}
                        fullWidth
                        type="text"
                        defaultValue={embedCodeTmp}
                    />
                </div>
                <div className={`my-16`}>
                    <p className={`mb-10 text-14 leading-20 text-primary-80`}>QR Code</p>
                    <p className={`mb-10 text-10 leading-12 text-primary-70`}>Scan the QR Code to view your menu.</p>
                    <div className={`mt-10 bg-primary-10 rounded p-16 flex items-center justify-between`}>
                        <div className={`flex-shrink-0 w-80 h-80 rounded overflow-hidden`}>
                            <ZenQRCode value={qrUrl} size={1500} />
                        </div>
                        <div
                            className="flex-auto text-16 p-10 flex items-center justify-end cursor-pointer"
                            onClick={() => downloadQRCode(name)}
                        >
                            <div className="mr-8">
                                <DownloadIcon />
                            </div>
                            <p>Download</p>
                        </div>
                    </div>
                </div>
                {dashboardUrl && (
                    <div className={`flex justify-center mt-16`}>
                        <Link to={dashboardUrl} className="w-full">
                            <Button color={`primary`} variant={`contained`} fullWidth>
                                {'Go to Dashboard'} <FontAwesomeIcon className={'ml-10'} icon={faArrowRight} />
                            </Button>
                        </Link>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default ShareModal;
